import { useState } from 'react';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import DataCategoryTabs from './components/DataCategoryTabs';
import DataGridToolbar from './components/DataGridToolbar';
import SuppliersDataGrid from './components/SuppliersDataGrid';
import BuyersDataGrid from './components/BuyersDataGrid';
import PortsDataGrid from './components/PortsDataGrid';
import VesselsDataGrid from './components/VesselsDataGrid';
import EnablersDataGrid from './components/EnablersDataGrid';
import ConfigurationsDataGrid from './components/ConfigurationsDataGrid';
import IntegrationStatusPanel from './components/IntegrationStatusPanel';
import TopNavbar from '../../components/ui/TopNavbar';

const MasterDataManagement = () => {
  const [activeTab, setActiveTab] = useState('suppliers');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleAdd = () => {
    console.log(`Add new ${activeTab}`);
  };

  const handleBulkImport = () => {
    console.log(`Bulk import ${activeTab}`);
  };

  const handleExport = () => {
    console.log(`Export ${activeTab} data`);
  };

  const handleEdit = (item) => {
    console.log('Edit item:', item);
  };

  const handleDelete = (id) => {
    console.log('Delete item:', id);
  };

  const handleBulkDelete = () => {
    console.log('Bulk delete items:', selectedItems);
    setSelectedItems([]);
  };

  const getCategoryLabel = () => {
    switch (activeTab) {
      case 'suppliers': return 'Supplier';
      case 'buyers': return 'Buyer';
      case 'ports': return 'Port';
      case 'vessels': return 'Vessel';
      case 'enablers': return 'Enabler';
      case 'configurations': return 'Configuration';
      default: return 'Item';
    }
  };

  const renderDataGrid = () => {
    const commonProps = {
      searchQuery,
      onEdit: handleEdit,
      onDelete: handleDelete,
      selectedItems,
      onSelectionChange: setSelectedItems
    };

    switch (activeTab) {
      case 'suppliers':
        return <SuppliersDataGrid {...commonProps} />;
      case 'buyers':
        return <BuyersDataGrid {...commonProps} />;
      case 'ports':
        return <PortsDataGrid {...commonProps} />;
      case 'vessels':
        return <VesselsDataGrid {...commonProps} />;
      case 'enablers':
        return <EnablersDataGrid {...commonProps} />;
      case 'configurations':
        return <ConfigurationsDataGrid {...commonProps} />;
      default:
        return null;
    }
  };

  const handleAddRecord = (category) => {
    console.log('Adding new record to:', category);
    setShowAddModal(true);
    setSelectedCategory(category);
  };

  const handleSaveRecord = (recordData) => {
    console.log('Saving record:', recordData);
    // TODO: Implement actual save with Supabase
    alert('✅ Record added successfully!');
    setShowAddModal(false);
  };

  const handleEditRecord = (category, recordId) => {
    console.log('Editing record:', category, recordId);
    setShowEditModal(true);
    // TODO: Load record data for editing
  };

  const handleUpdateRecord = (recordData) => {
    console.log('Updating record:', recordData);
    // TODO: Implement actual update
    alert('✅ Record updated successfully!');
    setShowEditModal(false);
  };

  const handleDeleteRecord = (category, recordId) => {
    if (confirm('Are you sure you want to delete this record?')) {
      console.log('Deleting record:', category, recordId);
      // TODO: Implement deletion
      alert('✅ Record deleted!');
    }
  };

  const handleImportData = (category) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx';
    input.onchange = (e) => {
      const file = e?.target?.files?.[0];
      console.log('Importing data:', category, file);
      // TODO: Implement bulk import
      alert('✅ Data import completed!');
    };
    input?.click();
  };

  const handleExportData = (category) => {
    console.log('Exporting data:', category);
    // TODO: Implement export
    alert(`✅ Exporting ${category} data...`);
  };

  const handleSyncIntegrations = () => {
    console.log('Syncing integrations');
    // TODO: Implement integration sync
    alert('✅ Integration sync started!');
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      <TopNavbar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header Section - positioned below TopNavbar */}
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm mt-[65px]">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Database" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-foreground">Master Data Management</h1>
                  <p className="text-sm text-muted-foreground">
                    Centralized reference data administration and system configurations
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="default"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={() => console.log('Sync all systems')}
                >
                  Sync All
                </Button>
                <Button
                  variant="outline"
                  size="default"
                  iconName="FileDown"
                  iconPosition="left"
                  onClick={() => console.log('Download template')}
                >
                  Templates
                </Button>
              </div>
            </div>
          </div>

          <DataCategoryTabs activeTab={activeTab} onTabChange={setActiveTab} />
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <IntegrationStatusPanel />

            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <DataGridToolbar
                onSearch={handleSearch}
                onAdd={handleAdd}
                onBulkImport={handleBulkImport}
                onExport={handleExport}
                selectedCount={selectedItems?.length}
                onBulkDelete={handleBulkDelete}
                categoryLabel={getCategoryLabel()}
              />

              <div className="overflow-hidden">
                {renderDataGrid()}
              </div>

              <div className="px-6 py-4 border-t border-border bg-muted/30">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Icon name="Info" size={16} />
                    <span>
                      Use keyboard shortcuts: Ctrl+1-5 to switch tabs, Ctrl+N to add new record
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span>Last updated: 2025-12-13 22:19:00</span>
                    <div className="flex items-center gap-1">
                      <Icon name="CheckCircle2" size={16} className="text-success" />
                      <span>All systems operational</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterDataManagement;